/**
 * ピース 1 枚ぶんの DOM。
 *
 * ピースは 1 要素 1 個の `div` で、原画像を `background-image`、断片の切り出しを
 * `background-position`、盤面上の位置を `transform: translate3d()` で与える（ADR-007）。
 * 秘文字断片と 4 辺の継ぎ目は子要素として持つため、ピースと一緒に移動する（FR-012）。
 *
 * **毎回書き換えるのは位置（`--px` / `--py`）と継ぎ目の表示だけ**である。背景の切り出し位置は
 * ピースに固有で、盤面上をどう動いても変わらない。
 *
 * 根拠: spec/04-architecture.md（board-view / piece-view）、02-requirements.md FR-006 FR-012 FR-021。
 */

import { BOARD_H, BOARD_W } from '../core/constants';
import { cellOf, col, row } from '../core/grid';
import type { CellIndex, PieceId } from '../core/types';

/** ピース 1 枚の論理寸法。 */
export interface PieceSize {
  readonly w: number;
  readonly h: number;
}

/**
 * 分割数からピースの論理寸法を求める。
 *
 * 論理盤面 360 × 480 は、許可された全分割数で境界が整数に落ちるように選ばれている（ADR-014）。
 * 非整数になったら、それは分割数の追加時に整数性の検算を怠ったということなので、黙って
 * 丸めずに落とす。
 */
export function pieceSize(C: number, R: number): PieceSize {
  const w = BOARD_W / C;
  const h = BOARD_H / R;
  if (!Number.isInteger(w) || !Number.isInteger(h)) {
    throw new Error(
      `分割数 ${C}:${R} ではピース境界が整数になりません（${w} × ${h}）。ADR-014 の整数性を満たす分割数だけを使ってください。`,
    );
  }
  return { w, h };
}

/** ピースの 4 辺。 */
export const EDGES = ['top', 'right', 'bottom', 'left'] as const;
export type Edge = (typeof EDGES)[number];

/** 各辺の隣接セルへの変位（列, 行）。 */
export const EDGE_DELTA: Readonly<Record<Edge, readonly [number, number]>> = {
  top: [0, -1],
  right: [1, 0],
  bottom: [0, 1],
  left: [-1, 0],
};

/** 生成したピースの DOM 一式。 */
export interface PieceElements {
  readonly root: HTMLElement;
  readonly seams: Readonly<Record<Edge, HTMLElement>>;
}

/**
 * ピース `piece` の要素を作る。
 *
 * @param piece ピース ID。原画像のセル `piece` の断片を表示する（`04a` §1.2）
 * @param secretCell 重ねる秘文字断片のセル。第 1 段階の目標配置 `τ[piece]`（`04a` §1.5）
 */
export function createPiece(
  piece: PieceId,
  secretCell: CellIndex,
  C: number,
  size: PieceSize,
  tileImage?: string,
): PieceElements {
  const root = document.createElement('div');
  root.className = 'piece';
  root.dataset.piece = String(piece);
  if (tileImage !== undefined) {
    // ピース別タイル（TK-035）: 原画像をプレイ画面の DOM に現れさせない。
    root.classList.add('piece--tiled');
    root.style.setProperty('--tile-image', tileImage);
  } else {
    // タイルの無いステージ（開発用の仮ステージ等）: 従来どおり原画像から切り出す。
    root.style.setProperty('--img-x', `${-col(piece, C) * size.w}px`);
    root.style.setProperty('--img-y', `${-row(piece, C) * size.h}px`);
  }

  // 継ぎ目は秘文字より奥に置く（FR-021）。DOM 順がそのまま重なり順になる。
  const seams = {} as Record<Edge, HTMLElement>;
  for (const edge of EDGES) {
    const seam = document.createElement('div');
    seam.className = `seam seam--${edge}`;
    seams[edge] = seam;
    root.append(seam);
  }

  const secret = document.createElement('div');
  secret.className = 'piece-secret';
  secret.style.setProperty('--sec-x', `${-col(secretCell, C) * size.w}px`);
  secret.style.setProperty('--sec-y', `${-row(secretCell, C) * size.h}px`);
  root.append(secret);

  return { root, seams };
}

/** ピースを盤面上のセル `cell` へ置く。 */
export function setPieceCell(
  elements: PieceElements,
  cell: CellIndex,
  C: number,
  size: PieceSize,
): void {
  setPiecePoint(elements.root, col(cell, C) * size.w, row(cell, C) * size.h);
}

/**
 * ピースを論理座標の任意の点へ置く。ドラッグ中のポインタ追従（TK-011）が使う。
 *
 * 書き換えるのは `--px` / `--py` だけである。位置は `transform: translate3d()` にのみ
 * 影響し、レイアウトを汚さない。
 */
export function setPiecePoint(root: HTMLElement, x: number, y: number): void {
  root.style.setProperty('--px', `${x}px`);
  root.style.setProperty('--py', `${y}px`);
}

/**
 * 継ぎ目の表示状態を切り替える（FR-021、TK-040）。
 *
 * - `normal`: ベベルを秘文字より手前に描く（未結合の辺。ピースが 1 枚ずつに見える）
 * - `under`: ベベルを秘文字より奥に描く（第 1 段階の結合辺。秘文字が辺をまたいで連続する）
 * - `joined`: 消す（第 2 段階の結合辺）。消えるときはフェードする
 */
export function setSeamState(
  elements: PieceElements,
  edge: Edge,
  state: 'normal' | 'under' | 'joined',
): void {
  const seam = elements.seams[edge];
  seam.classList.toggle('seam--joined', state === 'joined');
  seam.classList.toggle('seam--under', state === 'under');
}

/** セル `cell` の `edge` 方向の隣接セル。盤外なら `null`。 */
export function neighborCell(cell: CellIndex, edge: Edge, C: number, R: number): CellIndex | null {
  const [dc, dr] = EDGE_DELTA[edge];
  const c = col(cell, C) + dc;
  const r = row(cell, C) + dr;
  if (c < 0 || c >= C || r < 0 || r >= R) return null;
  return cellOf(c, r, C);
}
